<template>
	<div ref="container" class="focus-trap">
		<slot></slot>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps<{
	onKeydown?: (e: KeyboardEvent) => void
	active: boolean
}>()

const container = ref<HTMLElement | null>(null)
const lastFocusedEl = ref<HTMLElement | null>(null)

const getFocusableElements = (root: HTMLElement | null) => {
	if (!root) return []
	return Array.from(
		root.querySelectorAll<HTMLElement>(
			'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		)
	).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'))
}

let handleKeydown = (event: KeyboardEvent) => {
	if (!props.active) return

	if (event.key === 'Tab') {
		const focusableEls = getFocusableElements(container.value)
		if (!focusableEls.length) {
			event.preventDefault()
			return
		}

		const firstEl = focusableEls[0]
		const lastEl = focusableEls[focusableEls.length - 1]

		if (event.shiftKey) {
			if (document.activeElement === firstEl) {
				event.preventDefault()
				lastEl.focus()
			}
		} else {
			if (document.activeElement === lastEl) {
				event.preventDefault()
				firstEl.focus()
			}
		}
	}

	if (props.onKeydown) props.onKeydown(event)
}

watch(
	() => props.active,
	(val) => {
		if (val) {
			lastFocusedEl.value = document.activeElement as HTMLElement | null
			nextTick(() => {
				const focusableEls = getFocusableElements(container.value)
				if (focusableEls.length) focusableEls[0].focus()
				else container.value?.setAttribute('tabindex', '-1') && container.value.focus()
			})
			document.addEventListener('keydown', handleKeydown)
		} else {
			document.removeEventListener('keydown', handleKeydown)
			if (lastFocusedEl.value) lastFocusedEl.value.focus()
		}
	},
	{ immediate: true }
)

onBeforeUnmount(() => {
	document.removeEventListener('keydown', handleKeydown)
	if (lastFocusedEl.value) lastFocusedEl.value.focus()
})
</script>

<style scoped>
.focus-trap {
	outline: none;
}
</style>
